import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane
} from 'reactstrap';
import { UsersTab } from './UsersTab';
import { LoginLogoutTab } from './LoginLogoutTab';
import { InputDataTab } from './InputDataTab';
import { MaintenanceTab } from './MaintenanceTab/MaintenanceTab';
import { PlannedTab } from './PlannedTab';
import './AdminPanel.css';

const TABS = [
    {
        id: 'users',
        label: 'Пользователи',
        component: UsersTab
    },
    {
        id: 'logs',
        label: 'Логи авторизации',
        component: LoginLogoutTab
    },
    {
        id: 'inputData',
        label: 'История обработок',
        component: InputDataTab
    },
    {
        id: 'planned',
        label: 'Запланированные задачи',
        component: PlannedTab
    },
    {
        id: 'maintenance',
        label: 'Настройки системы',
        component: MaintenanceTab
    }
];

export function AdminPanel() {
    const [activeTab, setActiveTab] = useState('users');
    const tabRefs = useRef(
        TABS.reduce((acc, tab) => {
            acc[tab.id] = React.createRef();
            return acc;
        }, {})
    );

    const setTab = useCallback((tabId) => {
        setActiveTab(prev => (prev === tabId ? prev : tabId));
    }, []);

    const focusTab = useCallback((tabId) => {
        requestAnimationFrame(() => {
            const ref = tabRefs.current[tabId];
            ref?.current?.focus();
        });
    }, []);

    const handleKeyDown = useCallback((e, tabId) => {
        const tabs = TABS.map(t => t.id);
        const currentIndex = tabs.indexOf(activeTab);

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setTab(tabId);
            focusTab(tabId);
            return;
        }

        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();

            const nextIndex =
                e.key === 'ArrowRight'
                    ? (currentIndex + 1) % tabs.length
                    : (currentIndex - 1 + tabs.length) % tabs.length;

            const nextTab = tabs[nextIndex];

            setTab(nextTab);
            focusTab(nextTab);
            return;
        }

        if (e.key === 'Home') {
            e.preventDefault();
            setTab('users');
            focusTab('users');
            return;
        }

        if (e.key === 'End') {
            e.preventDefault();
            setTab('maintenance');
            focusTab('maintenance');
        }
    }, [activeTab, setTab, focusTab]);

    const renderNav = useMemo(() => {
        return TABS.map(tab => (
            <NavItem key={tab.id} role="presentation">
                <NavLink
                    innerRef={tabRefs.current[tab.id]}
                    active={activeTab === tab.id}
                    onClick={() => setTab(tab.id)}
                    onKeyDown={(e) => handleKeyDown(e, tab.id)}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`${tab.id}-tabpanel`}
                    id={`${tab.id}-tab`}
                    tabIndex={activeTab === tab.id ? 0 : -1}
                    className={activeTab === tab.id ? 'active' : ''}
                    style={{ outline: 'none' }}
                >
                    {tab.label}
                </NavLink>
            </NavItem>
        ));
    }, [activeTab, handleKeyDown, setTab]);

    const renderTabs = useMemo(() => {
        return TABS.map(tab => {
            const Component = tab.component;

            return (
                <TabPane
                    key={tab.id}
                    tabId={tab.id}
                    role="tabpanel"
                    id={`${tab.id}-tabpanel`}
                    aria-labelledby={`${tab.id}-tab`}
                >
                    <Row>
                        <Col sm="12">
                            <Component />
                        </Col>
                    </Row>
                </TabPane>
            );
        });
    }, []);

    return (
        <Container className="admin-panel mt-4">
            <Helmet>
                <title>Административная панель</title>
            </Helmet>

            <Card>
                <CardHeader>
                    <h4>Административная панель</h4>
                </CardHeader>

                <CardBody>
                    <Nav tabs role="tablist">
                        {renderNav}
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        {renderTabs}
                    </TabContent>
                </CardBody>
            </Card>
        </Container>
    );
}